import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bell, FileText, Send, Settings as SettingsIcon, Shield } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { BillingTopTabs } from '@/components/billing/billingChrome';
import {
  AddonCardsSkeleton,
  BillingPlanHeroSkeleton,
  InvoicesTabSkeleton,
  PlanCardsSkeleton,
} from '@/components/billing/BillingSkeleton';
import {
  ACCOUNT_SETTINGS_SECTIONS,
  parseAccountSettingsSection,
  type AccountSettingsSection,
} from '@/lib/account/accountSettingsNav';

/**
 * The route-level placeholder for Account settings (`/home/account-settings`).
 *
 * Every section used to open on the bare `<Suspense>` spinner and a blank
 * `RoleRoute` frame before the page painted — and Plans and Billing then added
 * their own centred spinner. This draws the page's real nav (same active item)
 * beside the requested section's skeleton: the plan cards, or for Billing the
 * real tab strip with the Overview / Invoices / Add-ons shape the URL asks for.
 * Imported eagerly into `App.tsx`, so keep it light.
 */

const SECTION_ICON: Record<AccountSettingsSection, React.ComponentType<{ className?: string }>> = {
  general: SettingsIcon,
  security: Shield,
  notifications: Bell,
  plans: Send,
  billing: FileText,
};

const BILLING_TABS = [
  { label: 'Overview', value: 'overview' },
  { label: 'Invoices', value: 'invoices' },
  { label: 'Add-ons', value: 'addons' },
];

/** A card of label + field rows — the general / security / notifications forms. */
const FormCardSkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <div className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white">
    <div className="space-y-1.5 border-b border-[#EAECF0] px-5 py-4 sm:px-6 sm:py-5">
      <Skeleton className="h-4 w-36 rounded" />
      <Skeleton className="h-3.5 w-64 max-w-full rounded" />
    </div>
    <div className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="space-y-1.5">
          <Skeleton className="h-3.5 w-28 rounded" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

const SectionSkeleton: React.FC<{ section: AccountSettingsSection; billingTab: string }> = ({ section, billingTab }) => {
  if (section === 'plans') {
    return (
      <div className="px-[15px] py-6">
        <PlanCardsSkeleton />
      </div>
    );
  }
  if (section === 'billing') {
    const tab = BILLING_TABS.some((t) => t.value === billingTab) ? billingTab : 'overview';
    return (
      <div className="space-y-4">
        <BillingTopTabs tabs={BILLING_TABS} activeTab={tab} onTabChange={() => {}} />
        <div className="space-y-4 px-[15px] pb-6 sm:space-y-5">
          {tab === 'invoices' ? (
            <InvoicesTabSkeleton />
          ) : tab === 'addons' ? (
            <AddonCardsSkeleton />
          ) : (
            <BillingPlanHeroSkeleton />
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-3xl space-y-4 px-[15px] py-6">
      <FormCardSkeleton rows={section === 'notifications' ? 3 : 4} />
      {section === 'notifications' ? <FormCardSkeleton rows={5} /> : null}
    </div>
  );
};

export const AccountSettingsPageSkeleton: React.FC = () => {
  const [searchParams] = useSearchParams();
  const section = parseAccountSettingsSection(searchParams.get('section'));

  return (
    <div
      className="flex min-h-full w-full bg-white"
      aria-busy="true"
      aria-label="Loading account settings"
      data-testid="account-settings-page-skeleton"
    >
      <aside className="sticky top-0 z-10 hidden h-[calc(100dvh-60px)] w-[240px] shrink-0 self-start overflow-y-auto border-r border-[#EAECF0] bg-white px-3 py-6 md:block">
        <div className="px-2 pb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#98A2B3]">
          Account settings
        </div>
        <div className="space-y-0.5">
          {ACCOUNT_SETTINGS_SECTIONS.map((s) => {
            const Icon = SECTION_ICON[s.id];
            const active = s.id === section;
            return (
              <div
                key={s.id}
                className={cn(
                  'flex h-10 w-full items-center gap-2.5 rounded-lg px-2.5 text-[14px] font-medium',
                  active ? 'bg-brand-soft text-[#101828]' : 'text-[#344054]',
                )}
              >
                <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-brand' : 'text-[#667085]')} />
                <span className="truncate">{s.label}</span>
              </div>
            );
          })}
        </div>
      </aside>

      <div className="min-w-0 flex-1 bg-white">
        <div className="mb-4 px-[15px] pt-3 md:hidden">
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
        <SectionSkeleton section={section} billingTab={searchParams.get('tab') || 'overview'} />
      </div>
    </div>
  );
};

export default AccountSettingsPageSkeleton;
